<template>
  <div class="fgrp-page">
    <!-- ==================== 概览头 ==================== -->
    <div class="fgp-hero">
      <div class="fgp-hero-l">
        <div class="fgp-hero-title">
          <el-button icon="ArrowLeft" text @click="go({ path: '/formList' })">返回</el-button>
          <h2>拼团列表</h2>
          <span class="fgp-hero-badge">拼团</span>
        </div>
        <p class="fgp-hero-sub">
          拼团只在开启「拼团」能力的表单上产生。成团判定靠成员数的原子更新，
          达到成团人数后状态置为「已成团」（幂等，重复触发不变）。
        </p>
      </div>
      <div class="fgp-hero-r">
        <el-button icon="Refresh" @click="refresh">刷 新</el-button>
      </div>
    </div>

    <!-- ==================== 筛选条 ==================== -->
    <div class="fgp-bar">
      <el-select v-model="query.formId" clearable filterable placeholder="全部表单" class="fgp-sel-form">
        <el-option v-for="f in formOptions" :key="f.id" :label="f.title" :value="f.id"></el-option>
      </el-select>
      <el-select v-model="query.state" clearable placeholder="全部状态" class="fgp-sel-state">
        <el-option v-for="s in stateOptions" :key="s.value" :label="s.label" :value="s.value"></el-option>
      </el-select>
      <el-input
          v-model="query.keyword"
          clearable
          placeholder="团号"
          prefix-icon="Search"
          class="fgp-keyword"
          @keyup.enter="search"></el-input>
      <el-button type="primary" icon="Search" @click="search">查询</el-button>
      <el-button icon="RefreshLeft" @click="reset">重置</el-button>
      <span class="fgp-count">共 {{ pageTotal }} 条</span>
    </div>

    <!-- ==================== 表格 ==================== -->
    <div class="fgp-card">
      <el-table
          v-loading="listLoading"
          :data="tableData"
          :cell-style="{padding: '6px 0'}"
          row-key="id"
          class="fgp-table">
        <el-table-column min-width="160" show-overflow-tooltip label="表单">
          <template #default="scope">{{ scope.row.formTitle || '—' }}</template>
        </el-table-column>
        <el-table-column width="150" label="团号">
          <template #default="scope"><span class="fgp-mono">{{ scope.row.groupNo || '—' }}</span></template>
        </el-table-column>
        <el-table-column width="150" label="团长">
          <template #default="scope">
            <span v-if="scope.row.leaderName">{{ scope.row.leaderName }}</span>
            <span v-else class="fgp-mono">记录 {{ scope.row.leaderRecordId || '—' }}</span>
          </template>
        </el-table-column>
        <el-table-column width="180" label="成员 / 成团">
          <template #default="scope">
            <div class="fgp-members">
              <div class="fgp-members-top">
                <span class="fgp-members-num">{{ scope.row.memberCount || 0 }}</span>
                <span class="fgp-members-cap">/ {{ scope.row.minMembers || 0 }} 成团</span>
                <span v-if="scope.row.maxMembers" class="fgp-members-cap">（上限 {{ scope.row.maxMembers }}）</span>
              </div>
              <div class="fgp-prog">
                <span :style="{ width: memberPercent(scope.row), background: memberColor(scope.row) }"></span>
              </div>
            </div>
          </template>
        </el-table-column>
        <el-table-column width="100" align="center" label="状态">
          <template #default="scope">
            <el-tag :type="stateTagType(scope.row.state)" size="small">{{ stateText(scope.row.state) }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column width="160" label="截止时间">
          <template #default="scope">{{ scope.row.deadline || '—' }}</template>
        </el-table-column>
        <el-table-column width="160" label="创建时间">
          <template #default="scope">{{ scope.row.createdAt || '—' }}</template>
        </el-table-column>

        <template #empty>
          <div v-if="listFailed" class="fgp-fail">
            <el-icon><WarningFilled /></el-icon>
            <p>{{ listError || '拼团列表加载失败' }}</p>
            <el-button type="primary" icon="RefreshRight" @click="selectList">重试</el-button>
          </div>
          <div v-else class="fgp-empty">
            <el-icon><Checked /></el-icon>
            <p>{{ emptyText }}</p>
            <el-button v-if="hasFilter" text type="primary" @click="reset">重置筛选</el-button>
          </div>
        </template>
      </el-table>
    </div>

    <div class="pagination">
      <el-pagination
          background
          layout="total, sizes, prev, pager, next, jumper"
          :page-sizes="[10, 20, 50, 100]"
          :current-page="query.pageIndex"
          :page-size="query.pageSize"
          :total="pageTotal"
          @size-change="handleSizeChange"
          @current-change="handlePageChange"></el-pagination>
    </div>
  </div>
</template>

<script>
import { formPage, formGroupPage, GROUP_SIGN_STATE_TEXT } from '@/api/form';

export default {
  name: 'FormGroup',
  data() {
    return {
      listLoading: false,
      listFailed: false,
      listError: '',
      tableData: [],
      pageTotal: 0,
      formOptions: [],
      stateDict: GROUP_SIGN_STATE_TEXT,
      query: {
        pageIndex: 1,
        pageSize: 10,
        formId: null,
        state: null,
        keyword: '',
      },
    };
  },
  computed: {
    stateOptions() {
      return Object.keys(GROUP_SIGN_STATE_TEXT).map(k => ({
        value: Number(k),
        label: GROUP_SIGN_STATE_TEXT[k],
      }));
    },
    hasFilter() {
      return this.query.formId != null || this.query.state != null || !!this.query.keyword;
    },
    emptyText() {
      if (this.hasFilter) return '没有符合筛选条件的拼团';
      return '还没有拼团 —— 拼团是表单级可选项，未开启拼团的表单不会产生团';
    },
  },
  methods: {
    stateText(s) {
      return GROUP_SIGN_STATE_TEXT[s] || '未知';
    },
    stateTagType(s) {
      if (s === 2) return 'success';
      if (s === 3) return 'danger';
      return 'warning';
    },
    memberPercent(row) {
      const min = Number(row.minMembers) || 0;
      const cur = Number(row.memberCount) || 0;
      if (min <= 0) return 0;
      return Math.min(100, Math.round((cur / min) * 100));
    },
    memberColor(row) {
      if (row.state === 2) return 'var(--el-color-success, #67c23a)';
      if (row.state === 3) return 'var(--el-color-danger, #f56c6c)';
      return 'var(--yh-theme, #8dc63f)';
    },
    loadFormOptions() {
      formPage({ pageIndex: 1, pageSize: 200, keyword: '' }).then(val => {
        if (val && val.code === 200) {
          const records = (val.data && val.data.records) || [];
          this.formOptions = records.map(r => ({ id: r.id, title: r.title || ('#' + r.id) }));
        }
      }).catch(() => { /* 下拉拉取失败不影响主列表 */ });
    },
    selectList() {
      this.listLoading = true;
      this.listFailed = false;
      this.listError = '';
      formGroupPage(this.query).then(val => {
        this.listLoading = false;
        if (val && val.code === 200) {
          this.tableData = (val.data && val.data.records) || [];
          this.pageTotal = (val.data && val.data.total) || 0;
        } else {
          this.tableData = [];
          this.pageTotal = 0;
          this.listFailed = true;
          this.listError = (val && val.message) || '拼团列表加载失败';
        }
      }).catch((err) => {
        this.listLoading = false;
        this.tableData = [];
        this.pageTotal = 0;
        this.listFailed = true;
        this.listError = (err && err.message) || '拼团列表加载失败，请检查网络';
      });
    },
    refresh() {
      this.selectList();
    },
    search() {
      this.query.pageIndex = 1;
      this.refresh();
    },
    reset() {
      this.query.formId = null;
      this.query.state = null;
      this.query.keyword = '';
      this.query.pageIndex = 1;
      this.refresh();
    },
    handleSizeChange(size) {
      this.query.pageSize = size;
      this.query.pageIndex = 1;
      this.selectList();
    },
    handlePageChange(index) {
      this.query.pageIndex = index;
      this.selectList();
    },
    go(target) {
      const p = this.$router.push(target);
      if (p && typeof p.catch === 'function') {
        p.catch(err => {
          const msg = (err && err.message) || '未知错误';
          this.$message.error(`打开页面失败：${msg}（若是改版后首次进入，请按 Ctrl+F5 强制刷新）`);
        });
      }
    },
  },
  created() {
    const q = this.$route.query.formId;
    this.query.formId = (q != null && q !== '' && !Number.isNaN(Number(q))) ? Number(q) : null;
    this.loadFormOptions();
    this.selectList();
  },
};
</script>

<style scoped>

/* @form-tokens:start */
/* ======================= 表单模块设计令牌（v3.11 视觉统一）=======================
 * ★ 本块在多个表单页各存一份（CSS 变量按作用域隔离，无法跨 scoped 共享）。
 *   改配色的唯一入口 = D:/tmp/shot/form_tokens.py 的 TOKEN_BLOCK，改完重跑该脚本
 *   一键同步；**不要手改单页**，否则多份会漂移。
 * ★ 为什么不放全局 :root：--el-color-primary 一旦全局生效会波及后台所有模块
 *   （订单 / 教练 / 财务…）。本模块只对「表单」相关页负责，故挂在页面根容器上，
 *   作用域精确、零外溢。
 * ========================================================================== */
.fgrp-page {
  --el-color-primary: #8dc63f;
  --el-color-primary-light-3: #afd779;
  --el-color-primary-light-5: #c6e39f;
  --el-color-primary-light-7: #ddeec5;
  --el-color-primary-light-8: #e8f4d9;
  --el-color-primary-light-9: #f4f9ec;
  --el-color-primary-dark-2: #719e32;

  /* —— 文字五档（严格对齐 Element 灰阶） —— */
  --yh-t1: #303133; /* 主文字 */
  --yh-t2: #606266; /* 常规 */
  --yh-t3: #909399; /* 次要 */
  --yh-t4: #a8abb2; /* 占位 */
  --yh-t5: #c0c4cc; /* 禁用 */

  /* —— 主色四档 —— */
  --yh-theme: #8dc63f;
  --yh-theme-d: #7cad37;
  --yh-theme-dd: #5f9427;
  --yh-theme-l: #eef7e2;
  --yh-theme-ll: #f6fbef;

  /* —— 面与线 —— */
  --yh-bg: #f5f7fa;
  --yh-fill: #fafbfc;
  --yh-line: #ebeef5;
  --yh-line-d: #dcdfe6;
  --yh-divide: #f2f3f5;

  /* —— 圆角五档 —— */
  --yh-r-xs: 4px;
  --yh-r-sm: 6px;
  --yh-r: 8px;
  --yh-r-lg: 12px;
  --yh-r-pill: 999px;
}
/* @form-tokens:end */

.fgrp-page {
  padding: 16px 20px 30px;
  min-height: 100%;
  background-color: #f5f7fa;
  box-sizing: border-box;
}

/* ==================== 概览头 ==================== */
.fgp-hero {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 20px;
  flex-wrap: wrap;
  background: #fff;
  border-radius: var(--yh-r-lg);
  padding: 16px 20px;
  margin-bottom: 14px;
  box-shadow: 0 1px 4px rgba(0, 0, 21, 0.06);
}

.fgp-hero-title {
  display: flex;
  align-items: center;
  gap: 8px;
}

.fgp-hero-title h2 {
  margin: 0;
  font-size: 19px;
  font-weight: 600;
  color: var(--yh-t1);
}

.fgp-hero-badge {
  padding: 1px 8px;
  border-radius: var(--yh-r-pill);
  background: var(--yh-theme-l);
  color: var(--yh-theme-dd);
  font-size: 12px;
}

.fgp-hero-sub {
  margin: 6px 0 0;
  max-width: 780px;
  font-size: 12px;
  line-height: 1.8;
  color: var(--yh-t3);
}

.fgp-hero-r {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  flex: 0 0 auto;
}

/* ==================== 筛选条 ==================== */
.fgp-bar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  background: #fff;
  border-radius: var(--yh-r-lg);
  padding: 12px 16px;
  margin-bottom: 12px;
  box-shadow: 0 1px 4px rgba(0, 0, 21, 0.06);
}

.fgp-sel-form {
  width: 200px;
}

.fgp-sel-state {
  width: 130px;
}

.fgp-keyword {
  width: 170px;
}

.fgp-count {
  margin-left: auto;
  font-size: 12px;
  color: var(--yh-t3);
}

/* ==================== 表格卡 ==================== */
.fgp-card {
  background: #fff;
  border-radius: var(--yh-r-lg);
  padding: 6px 12px 14px;
  box-shadow: 0 1px 4px rgba(0, 0, 21, 0.06);
}

.fgp-table :deep(.el-table__inner-wrapper::before) {
  display: none;
}

.fgp-table :deep(.el-table__header th) {
  background: var(--yh-fill);
  color: var(--yh-t2);
  font-weight: 600;
}

.fgp-table :deep(.el-table__row:hover > td) {
  background: var(--yh-theme-ll) !important;
}

.fgp-mono {
  font-family: Consolas, Monaco, monospace;
  font-size: 12px;
  color: var(--yh-t2);
}

.fgp-members-top {
  display: flex;
  align-items: baseline;
  gap: 4px;
}

.fgp-members-num {
  font-size: 14px;
  font-weight: 600;
  color: var(--yh-t1);
}

.fgp-members-cap {
  font-size: 12px;
  color: var(--yh-t3);
}

.fgp-prog {
  height: 5px;
  margin-top: 5px;
  border-radius: var(--yh-r-pill);
  background: #f0f1f3;
  overflow: hidden;
}

.fgp-prog span {
  display: block;
  height: 100%;
  border-radius: var(--yh-r-pill);
  transition: width 0.3s;
}

.pagination {
  margin-top: 16px;
  text-align: right;
}

/* 空态 / 失败态 */
.fgp-empty,
.fgp-fail {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 26px 0 12px;
}

.fgp-empty .el-icon,
.fgp-fail .el-icon {
  font-size: 32px;
  color: #dcdfe6;
}

.fgp-fail .el-icon {
  color: #e6a23c;
}

.fgp-empty p,
.fgp-fail p {
  margin: 0;
  font-size: 13px;
  color: var(--yh-t4);
}
</style>
